/**
 * ExecuteAnonymizationModal — confirmação DURA antes de executar (irreversível).
 *
 * Pattern TypingToConfirmModal (A2 §7.7.1): o DPO digita "anonimizar" para
 * habilitar o botão. Anonimização é IRREVERSÍVEL (P15) — o execute move o
 * registro de `pending` → `in_progress` no servidor e dispara o cascade ADR-110.
 */

import { useState } from 'react';

import { ANONYMIZATION_LEVEL_META, type AnonymizationWireItem } from './types';

const CONFIRM_WORD = 'anonimizar';

interface ExecuteAnonymizationModalProps {
  item: AnonymizationWireItem;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  error: string | null;
}

export function ExecuteAnonymizationModal({
  item,
  onClose,
  onConfirm,
  isSubmitting,
  error,
}: ExecuteAnonymizationModalProps): JSX.Element {
  const [typed, setTyped] = useState('');
  const confirmed = typed.trim().toLowerCase() === CONFIRM_WORD;

  return (
    <div
      className="anon-scope anon-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if ((e.target as HTMLElement).classList.contains('anon-modal-scrim')) onClose();
      }}
    >
      <div
        className="anon-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Confirmar anonimização irreversível"
      >
        <div className="anon-modal__head">
          <div>
            <div className="anon-modal__title">Executar anonimização</div>
            <div className="anon-modal__sub">Operação irreversível · audit forense gerado</div>
          </div>
          <button type="button" className="anon-modal__close" aria-label="Fechar" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="anon-modal__body">
          <p className="anon-modal__warn" role="alert">
            Esta ação <strong>não pode ser desfeita</strong>. O nível{' '}
            <strong>{ANONYMIZATION_LEVEL_META[item.level].label}</strong> será aplicado a{' '}
            <code>
              {item.entityType} · {item.entityId}
            </code>{' '}
            e a anonimização em cascata será disparada.
          </p>

          <label className="anon-field">
            <span className="anon-field__label">
              Digite <strong>{CONFIRM_WORD}</strong> para confirmar
            </span>
            <input
              className="anon-input"
              value={typed}
              autoComplete="off"
              placeholder={CONFIRM_WORD}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>

          {error !== null && (
            <p className="anon-alert anon-alert--error" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="anon-modal__foot">
          <button type="button" className="anon-btn anon-btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="anon-btn anon-btn--danger"
            disabled={!confirmed || isSubmitting}
            onClick={onConfirm}
          >
            {isSubmitting ? 'Executando…' : 'Executar agora'}
          </button>
        </div>
      </div>
    </div>
  );
}
